/**
 * UGC stories ("See it in action") — thumbnail row + popup video player.
 * The popup is the theme's <basic-modal>; this element only wires up which story
 * to open, builds the Swiper on first open, and plays/pauses as slides change.
 *
 * Playback is started synchronously inside the user's click / swipe / keypress so
 * browsers treat it as gesture-initiated and allow sound. If a browser still refuses
 * unmuted autoplay, the video starts muted and a "tap for sound" button is shown.
 */
if (!customElements.get('ugc-stories')) {
  customElements.define(
    'ugc-stories',
    class UgcStories extends HTMLElement {
      constructor() {
        super();
        this.requestedIndex = 0;
        this.slider = null;
        this.isOpen = false;
      }

      connectedCallback() {
        this.modal = document.getElementById(this.dataset.modalId);
        if (!this.modal) return;

        this.autoplay = this.dataset.autoplay !== 'false';
        this.sound = this.dataset.sound !== 'false';

        // Capture phase so this runs BEFORE the modal's own click handler opens it:
        // remember which story was tapped and start it right away (gesture-bound = sound allowed).
        this.querySelectorAll('.ugc-stories__thumb').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.requestedIndex = parseInt(btn.dataset.storyIndex, 10) || 0;
            this.pauseAll(this.requestedIndex);
            this.playIndex(this.requestedIndex, { restart: true });
          }, true);
        });

        // <basic-modal> moves itself to <body> while open, so listen on the element itself.
        this.modal.addEventListener('modal:handleAfterShow', this.onShow.bind(this));
        this.modal.addEventListener('modal:handleAfterHide', this.onHide.bind(this));

        // Videos render without native controls (iOS dims the frame and overlays skip/pause
        // buttons for the first few seconds), so a tap on the video toggles play/pause and a
        // play icon shows while paused.
        this.modal.querySelectorAll('.ugc-stories__video').forEach((video) => {
          const slide = video.closest('.ugc-stories__slide');
          video.addEventListener('click', () => {
            if (video.paused) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
          video.addEventListener('play', () => slide.classList.remove('is-paused'));
          video.addEventListener('pause', () => slide.classList.add('is-paused'));
        });

        // Tap-for-sound fallback buttons (one per slide, hidden until needed).
        this.modal.querySelectorAll('.ugc-stories__unmute').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const slide = btn.closest('.ugc-stories__slide');
            const video = slide.querySelector('video');
            const iframe = slide.querySelector('iframe[data-src]');
            if (video) {
              video.muted = false;
              video.volume = 1;
              video.play().catch(() => {});
            } else if (iframe) {
              this.unmuteEmbed(iframe);
            }
            btn.hidden = true;
          });
        });
      }

      get container() {
        return this.modal.querySelector('.ugc-stories__slider');
      }

      // Phones and tablets refuse sound-on autoplay inside a cross-origin embed:
      // the tap never reaches YouTube/Vimeo's own frame. Start those muted so the
      // clip actually plays, then offer one tap for sound.
      get embedsNeedMuting() {
        return window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 768;
      }

      unmuteEmbed(iframe) {
        const win = iframe.contentWindow;
        if (!win) return;
        // YouTube iframe API
        win.postMessage(JSON.stringify({ event: 'command', func: 'unMute', args: [] }), '*');
        win.postMessage(JSON.stringify({ event: 'command', func: 'setVolume', args: [100] }), '*');
        // Vimeo player API
        win.postMessage(JSON.stringify({ method: 'setVolume', value: 1 }), '*');
        win.postMessage(JSON.stringify({ method: 'play' }), '*');
      }

      get slides() {
        return Array.from(this.container.querySelectorAll('.ugc-stories__slide'));
      }

      get activeIndex() {
        if (this.slider && this.slider.slider) return this.slider.slider.activeIndex;
        return this.requestedIndex;
      }

      onShow() {
        this.isOpen = true;
        this.initSlider();
        const swiper = this.slider && this.slider.slider;
        if (swiper) {
          swiper.update();
          if (swiper.activeIndex !== this.requestedIndex) swiper.slideTo(this.requestedIndex, 0);
        }
        // Already started in the click handler; this is a no-op if it's playing, and the
        // retry if the browser needed the modal visible first.
        this.playIndex(this.activeIndex);
      }

      onHide() {
        this.isOpen = false;
        this.pauseAll(-1);
      }

      initSlider() {
        if (this.slider || !this.container || typeof FoxTheme === 'undefined' || !FoxTheme.Carousel) return;
        if (this.slides.length < 2) return;

        this.slider = new FoxTheme.Carousel(this.container, {
          slidesPerView: 1,
          spaceBetween: 16,
          loop: false,
          threshold: 4,
          initialSlide: this.requestedIndex,
          navigation: {
            nextEl: this.container.querySelector('.swiper-button-next'),
            prevEl: this.container.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.container.querySelector('.swiper-pagination'),
            clickable: true,
          },
          on: {
            // Fires synchronously inside the swipe / arrow click, so sound is allowed.
            slideChange: (swiper) => {
              if (!this.isOpen) return;
              this.pauseAll(swiper.activeIndex);
              this.playIndex(swiper.activeIndex, { restart: true });
            },
          },
        });
        this.slider.init();

        // Arrow keys while the popup is open.
        this.modal.addEventListener('keydown', (e) => {
          if (!this.slider) return;
          if (e.key === 'ArrowRight') this.slider.slider.slideNext();
          if (e.key === 'ArrowLeft') this.slider.slider.slidePrev();
        });
      }

      playIndex(index, opts = {}) {
        const slide = this.slides[index];
        if (!slide) return;
        const video = slide.querySelector('video');
        const iframe = slide.querySelector('iframe[data-src]');
        const unmute = slide.querySelector('.ugc-stories__unmute');

        if (video) {
          if (unmute) unmute.hidden = true;
          video.preload = 'auto';
          video.muted = !this.sound;
          video.volume = 1;
          if (opts.restart) {
            try { video.currentTime = 0; } catch (e) { /* not loaded yet */ }
          }
          if (!this.autoplay) return;
          const attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') {
            attempt.catch(() => {
              if (!this.sound) return;
              // Browser refused sound-on autoplay: start muted and offer a tap-for-sound button.
              video.muted = true;
              video.play().then(() => {
                if (unmute) unmute.hidden = false;
              }).catch(() => {});
            });
          }
          // Warm the neighbours so switching is instant.
          [index - 1, index + 1].forEach((i) => {
            const n = this.slides[i] && this.slides[i].querySelector('video');
            if (n) n.preload = 'auto';
          });
        } else if (iframe) {
          // Mute in the URL when sound is off, or when the device would block an
          // unmuted embed outright (which leaves the slide frozen on a black frame).
          const mustMute = !this.sound || this.embedsNeedMuting;
          let src = iframe.dataset.src;
          if (this.autoplay) {
            src += (src.includes('?') ? '&' : '?') + 'autoplay=1';
            if (mustMute) src += '&mute=1&muted=1';
          }
          if (iframe.getAttribute('src') !== src) iframe.setAttribute('src', src);
          // Never auto-unmute an embed: YouTube/Vimeo HALT playback when told to
          // unmute without a real user gesture, which looks like the clip stopping
          // a second after it started. Sound only ever comes from the pill below.
          if (unmute) unmute.hidden = !(this.sound && mustMute);
        }
      }

      // Pause every slide except keepIndex (-1 = pause everything).
      pauseAll(keepIndex) {
        if (!this.container) return;
        this.slides.forEach((slide, i) => {
          if (i === keepIndex) return;
          const v = slide.querySelector('video');
          if (v) v.pause();
          const f = slide.querySelector('iframe[data-src]');
          if (f && f.getAttribute('src') !== 'about:blank') f.setAttribute('src', 'about:blank');
          const u = slide.querySelector('.ugc-stories__unmute');
          if (u) u.hidden = true;
        });
      }
    }
  );
}
